// ===========================================
// Admin API Functions
// ===========================================
// API calls for admin configuration endpoints

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface Term {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  /** Instant a live admin reopen expires (null/absent = never reopened). */
  reopenedUntil?: string | null;
  /** Server-derived lock state (GET /admin/terms). Absent on create/update responses. */
  lock?: TermLock;
  _count?: { Lesson: number };
}

export type TermLockStatus = 'OPEN' | 'LOCKED' | 'REOPENED';

export interface TermLock {
  status: TermLockStatus;
  lockedByTermId: string | null;
  lockedByTermName: string | null;
  /** The successor's start date (UTC-midnight @db.Date). */
  lockedSince: string | null;
  reopenedUntil: string | null;
  reopenedAt: string | null;
  reopenedByName: string | null;
  reopenReason: string | null;
  /** Ended on the school day (OPEN + ended = the holiday clean-up window). */
  ended: boolean;
  /** The successor, populated even while OPEN (null when no next term exists). `lockedBy*` is null while OPEN. */
  nextTermId: string | null;
  nextTermName: string | null;
  nextTermStartDate: string | null;
}

export interface Location {
  id: string;
  name: string;
  // Structured address fields
  streetAddress: string | null;
  unit: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string;
  // Legacy field (deprecated)
  address?: string | null;
  phone: string | null;
  // Geocoded coordinates (best-effort; null until geocoded on save).
  latitude?: number | null;
  longitude?: number | null;
  isActive: boolean;
  // GET /admin/locations nests rooms under the PascalCase Prisma relation `Room`
  // (filtered to active). `rooms` is a legacy alias kept for older callers.
  Room?: Room[];
  rooms?: Room[];
  _count?: { Room?: number; rooms?: number };
}

export interface Room {
  id: string;
  locationId: string;
  name: string;
  capacity: number;
  isActive: boolean;
  // PascalCase as `getRooms` sends it — the camelCase `location?` alias was never
  // sent, so the admin calendar's room view never showed a location (#205 family).
  Location?: Location;
}

export interface Instrument {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
}

export interface LessonType {
  id: string;
  name: string;
  type: 'INDIVIDUAL' | 'GROUP' | 'BAND' | 'HYBRID';
  defaultDuration: number;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
  // Partner classes: when true (INDIVIDUAL types only), two paired individual lessons for the
  // same teacher may overlap. Coerced to false server-side for non-INDIVIDUAL types.
  allowPartnerOverlap: boolean;
}

export interface LessonDuration {
  id: string;
  minutes: number;
  isActive: boolean;
}

export interface AgeGroup {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
}

export interface ProgramLevel {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
  displayName: string | null;
  threshold: number | null;
  mascot: string | null;
  mascotEmoji: string | null;
  isGamificationLevel: boolean;
  googleDriveFolder?: {
    id: string;
    folderName: string;
    folderUrl: string;
    driveFolderId: string;
    syncEnabled: boolean;
    _count?: { files: number };
  } | null;
}

export interface Stream {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
  /** Overrides the hybrid 1:1 clinic duration for classes in this stream; null = inherit the class's own. */
  individualSlotDuration: number | null;
}

export interface SchoolSettings {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  timezone: string;
  settings: Record<string, unknown>;
  branding: Record<string, unknown>;
  isActive: boolean;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// SCHOOL SETTINGS
// ===========================================

export const schoolApi = {
  getSettings: () =>
    apiClient.get<ApiResponse<SchoolSettings>>('/admin/school/settings')
      .then((res) => res.data),

  updateSettings: (data: Partial<SchoolSettings>) =>
    apiClient.patch<ApiResponse<SchoolSettings>>('/admin/school/settings', data)
      .then((res) => res.data),
};

// ===========================================
// TERMS
// ===========================================

export const termsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<Term[]>>('/admin/terms')
      .then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Term>>(`/admin/terms/${id}`)
      .then((res) => res.data),

  create: (data: { name: string; startDate: string; endDate: string }) =>
    apiClient.post<ApiResponse<Term>>('/admin/terms', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<Term>) =>
    apiClient.patch<ApiResponse<Term>>(`/admin/terms/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/terms/${id}`),

  /** ADMIN only. Suspends a lock until the end of the school day `days` (1-7) from today. */
  reopen: (id: string, data: { reason: string; days: number }) =>
    apiClient.post<ApiResponse<Term>>(`/admin/terms/${id}/reopen`, data)
      .then((res) => res.data),

  /** ADMIN only. "Lock now": ends a live reopen early (no-op when not reopened). */
  relock: (id: string) =>
    apiClient.post<ApiResponse<Term>>(`/admin/terms/${id}/lock`, {})
      .then((res) => res.data),
};

// ===========================================
// LOCATIONS
// ===========================================

export const locationsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<Location[]>>('/admin/locations')
      .then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Location>>(`/admin/locations/${id}`)
      .then((res) => res.data),

  create: (data: {
    name: string;
    streetAddress?: string;
    unit?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    country?: string;
    phone?: string;
    isActive?: boolean;
  }) =>
    apiClient.post<ApiResponse<Location>>('/admin/locations', data)
      .then((res) => res.data),

  update: (
    id: string,
    data: Partial<Location> & {
      // Manual pin override (both together) + re-geocode trigger ("Adjust pin").
      latitude?: number | null;
      longitude?: number | null;
      regeocode?: boolean;
    }
  ) =>
    apiClient.patch<ApiResponse<Location>>(`/admin/locations/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/locations/${id}`),
};

// ===========================================
// ROOMS
// ===========================================

export const roomsApi = {
  getAll: (locationId?: string) =>
    apiClient.get<ApiResponse<Room[]>>('/admin/rooms', {
      params: locationId ? { locationId } : undefined,
    }).then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Room>>(`/admin/rooms/${id}`)
      .then((res) => res.data),

  create: (data: { locationId: string; name: string; capacity?: number }) =>
    apiClient.post<ApiResponse<Room>>('/admin/rooms', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<Room>) =>
    apiClient.patch<ApiResponse<Room>>(`/admin/rooms/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/rooms/${id}`),
};

// ===========================================
// INSTRUMENTS
// ===========================================

export const instrumentsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<Instrument[]>>('/admin/instruments')
      .then((res) => res.data),

  create: (data: { name: string; sortOrder?: number; color?: string }) =>
    apiClient.post<ApiResponse<Instrument>>('/admin/instruments', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<Instrument>) =>
    apiClient.patch<ApiResponse<Instrument>>(`/admin/instruments/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/instruments/${id}`),

  reorder: (instrumentIds: string[]) =>
    apiClient.post<ApiResponse<Instrument[]>>('/admin/instruments/reorder', { instrumentIds })
      .then((res) => res.data),
};

// ===========================================
// LESSON TYPES
// ===========================================

export const lessonTypesApi = {
  getAll: () =>
    apiClient.get<ApiResponse<LessonType[]>>('/admin/lesson-types')
      .then((res) => res.data),

  create: (data: {
    name: string;
    type: 'INDIVIDUAL' | 'GROUP' | 'BAND' | 'HYBRID';
    defaultDuration: number;
    description?: string;
    color?: string;
    allowPartnerOverlap?: boolean;
  }) =>
    apiClient.post<ApiResponse<LessonType>>('/admin/lesson-types', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<LessonType>) =>
    apiClient.patch<ApiResponse<LessonType>>(`/admin/lesson-types/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/lesson-types/${id}`),
};

// ===========================================
// LESSON DURATIONS
// ===========================================

export const lessonDurationsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<LessonDuration[]>>('/admin/lesson-durations')
      .then((res) => res.data),

  create: (data: { minutes: number }) =>
    apiClient.post<ApiResponse<LessonDuration>>('/admin/lesson-durations', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<LessonDuration>) =>
    apiClient.patch<ApiResponse<LessonDuration>>(`/admin/lesson-durations/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/lesson-durations/${id}`),
};

// ===========================================
// AGE GROUPS
// ===========================================

export const ageGroupsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<AgeGroup[]>>('/admin/age-groups')
      .then((res) => res.data),

  create: (data: { name: string; minAge?: number; maxAge?: number; sortOrder?: number; color?: string }) =>
    apiClient.post<ApiResponse<AgeGroup>>('/admin/age-groups', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<AgeGroup>) =>
    apiClient.patch<ApiResponse<AgeGroup>>(`/admin/age-groups/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/age-groups/${id}`),

  reorder: (ageGroupIds: string[]) =>
    apiClient.post<ApiResponse<AgeGroup[]>>('/admin/age-groups/reorder', { ageGroupIds })
      .then((res) => res.data),
};

// ===========================================
// PROGRAM LEVELS
// ===========================================

export const programLevelsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<ProgramLevel[]>>('/admin/program-levels')
      .then((res) => res.data),

  create: (data: {
    name: string;
    sortOrder?: number;
    color?: string;
    displayName?: string;
    threshold?: number;
    mascot?: string;
    mascotEmoji?: string;
    isGamificationLevel?: boolean;
  }) =>
    apiClient.post<ApiResponse<ProgramLevel>>('/admin/program-levels', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<ProgramLevel>) =>
    apiClient.patch<ApiResponse<ProgramLevel>>(`/admin/program-levels/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/program-levels/${id}`),

  reorder: (programLevelIds: string[]) =>
    apiClient.post<ApiResponse<ProgramLevel[]>>('/admin/program-levels/reorder', { programLevelIds })
      .then((res) => res.data),
};

// ===========================================
// STREAMS
// ===========================================

export const streamsApi = {
  getAll: () =>
    apiClient.get<ApiResponse<Stream[]>>('/admin/streams')
      .then((res) => res.data),

  create: (data: { name: string; sortOrder?: number; color?: string | null; individualSlotDuration?: number | null }) =>
    apiClient.post<ApiResponse<Stream>>('/admin/streams', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<Stream>) =>
    apiClient.patch<ApiResponse<Stream>>(`/admin/streams/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/streams/${id}`),

  reorder: (streamIds: string[]) =>
    apiClient.post<ApiResponse<Stream[]>>('/admin/streams/reorder', { streamIds })
      .then((res) => res.data),
};

// ===========================================
// EVENT PRICING STRUCTURES
// ===========================================
// A school's named, reusable bundles of ticket tiers ("Concert Pricing",
// "Holiday Camp Pricing"). Structures SEED an event's price matrix; they never
// bind it — editing one does not retro-change an event that already exists.

export interface EventPricingStructureTier {
  id: string;
  key: string;
  label: string;
  defaultPriceCents: number;
  /**
   * Whether this KIND of tier stops selling on a day — a BOOLEAN, never a date.
   * The structure declares the shape ("Early Bird expires, Adult does not"); the
   * EVENT supplies the actual cutoff, because that day depends on the event's
   * own date. (Andrew, 2026-09-15.)
   */
  hasEndDate: boolean;
  sortOrder: number;
}

export interface EventPricingStructure {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  Tiers: EventPricingStructureTier[];
}

/** One tier as the editor submits it; the server resolves `key` when absent. */
export interface EventPricingStructureTierInput {
  key?: string;
  label: string;
  defaultPriceCents?: number;
  /** Whether this kind of tier expires. The event supplies the day. */
  hasEndDate?: boolean;
  sortOrder?: number;
}

export interface EventPricingStructureInput {
  name: string;
  description?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  tiers?: EventPricingStructureTierInput[];
}

export const eventPricingStructuresApi = {
  getAll: () =>
    apiClient.get<ApiResponse<EventPricingStructure[]>>('/admin/event-pricing-structures')
      .then((res) => res.data),

  create: (data: EventPricingStructureInput) =>
    apiClient.post<ApiResponse<EventPricingStructure>>('/admin/event-pricing-structures', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<EventPricingStructureInput>) =>
    apiClient.patch<ApiResponse<EventPricingStructure>>(`/admin/event-pricing-structures/${id}`, data)
      .then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/event-pricing-structures/${id}`),

  reorder: (structureIds: string[]) =>
    apiClient.post<ApiResponse<EventPricingStructure[]>>('/admin/event-pricing-structures/reorder', { structureIds })
      .then((res) => res.data),

  setDefault: (id: string) =>
    apiClient.post<ApiResponse<EventPricingStructure>>(`/admin/event-pricing-structures/${id}/default`, {})
      .then((res) => res.data),
};
